import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../../i18n/LanguageContext";

// Carta/menú en PDF subido desde el panel (/img/cartas.json). Si hay PDF en el
// idioma activo se usa; si no, el PDF en español. Si esa carta no tiene ningún
// PDF (o falla al cargar), se muestran los hijos: las imágenes de siempre.
//
// El PDF se pinta página a página con pdf.js en <canvas>, porque los visores
// nativos (iframe/embed) no funcionan bien en móvil: iOS solo enseña la primera
// página y Chrome Android no los muestra.

let manifestPromise = null;
function loadManifest() {
  if (!manifestPromise) {
    manifestPromise = fetch("/img/cartas.json?t=" + Date.now(), { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return manifestPromise;
}

export default function MenuPdf({ slug, className, alt, children }) {
  const { lang } = useI18n();
  const [manifest, setManifest] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    loadManifest().then((m) => alive && setManifest(m || {}));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => setFailed(false), [lang]);

  // Mientras se lee el manifiesto no pintamos nada, para no enseñar la imagen
  // antigua un instante y cambiarla luego por el PDF.
  if (manifest === null) return <div className="pdf-loading" />;

  const entry = manifest[slug] || {};
  const pdf = entry[lang] || entry.es;
  if (!pdf || !pdf.file || failed) return <>{children}</>;

  const src = "/img/" + encodeURIComponent(pdf.file) + "?v=" + (pdf.v || 0);
  return (
    <PdfPages
      key={src}
      src={src}
      className={className}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
}

function PdfPages({ src, className, alt, onError }) {
  const holder = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let doc = null;

    (async () => {
      try {
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf");
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          "pdfjs-dist/legacy/build/pdf.worker.min.js",
          import.meta.url
        ).toString();

        doc = await pdfjs.getDocument(src).promise;
        if (cancelled) return;

        // Resolución de render: nítido en móvil (retina) sin disparar memoria.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const targetPx = Math.min(2000, Math.max(1000, window.innerWidth * dpr));

        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: targetPx / base.width });

          const canvas = document.createElement("canvas");
          canvas.width = Math.round(viewport.width);
          canvas.height = Math.round(viewport.height);
          canvas.className = className || "";
          canvas.setAttribute("role", "img");
          canvas.setAttribute("aria-label", `${alt || "Carta"} · ${n}/${doc.numPages}`);

          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          if (cancelled) return;
          holder.current.appendChild(canvas);
          if (n === 1) setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          onError();
        }
      }
    })();

    const node = holder.current;
    return () => {
      cancelled = true;
      if (doc) doc.destroy();
      if (node) node.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  return (
    <>
      {loading && <div className="pdf-loading" />}
      <div ref={holder} className="pdf-pages" />
    </>
  );
}
